import {
    Button,
    DialogContentText,
    DialogTitle,
    Icon
} from "@material-ui/core";
import { ThemeProvider as MuiThemeProvider } from "@material-ui/core/styles";
import { Stripe } from "@stripe/stripe-js";
import React, { Component } from "react";
import styled from "styled-components";

import { MetricName } from "common/interfaces";
import Analytics from "js/analytics";
import BillingController, { BillingControllerState } from "js/controllers/BillingController";
import getLogger, { LogGroup } from "js/core/logger";
import { handleStripeErrors } from "js/core/services/stripe";
import { t } from "js/i18n";
import { BeautifulDialog, ShowDialogAsync, ShowErrorDialog } from "js/react/components/Dialogs/BaseDialog";
import FetchingClickShield from "js/react/components/FetchingClickShield";
import { dialogTheme } from "js/react/materialThemeOverrides";
import withStripe from "js/react/views/Billing/utils/withStripe";

import ChangePaymentMethodDialog from "./ChangePaymentMethodDialog";
import { PaddedDialogActions, PaddedDialogContent } from "./components/DialogComponents";

// @ts-ignore
import "css/billing.scss";

const logger = getLogger(LogGroup.BILLING);

const AlertContainer = styled.div`
  color: red;
  font-size: 15px;
  text-transform: uppercase;
  display: inline-flex;
  align-items: center;
`;

class ConfirmSubscriptionChargeDialog extends Component<{ onClose: (success: boolean) => void, stripe: Stripe } & BillingControllerState> {
    state = {
        fetching: false
    };

    componentDidMount() {
        Analytics.Dunning.ConfirmSubscriptionChargeDialogOpened();
    }

    handleChangePaymentMethod = async () => {
        const { onClose } = this.props;

        Analytics.Dunning.ConfirmSubscriptionChargeDialogChangePaymentMethodClicked();

        const result = await ShowDialogAsync(ChangePaymentMethodDialog);
        if (result !== false && result?.canceled !== true) {
            Analytics.Dunning.ConfirmSubscriptionChargeDialogPaymentMethodChanged();
            onClose(true);
        } else {
            onClose(false);
        }
    }

    handleConfirmPayment = async () => {
        const { onClose, stripe, stripeData: { subscription } } = this.props;

        Analytics.Dunning.ConfirmSubscriptionChargeDialogConfirmPaymentClicked();
        this.setState({ fetching: true });

        try {
            const latestInvoice = subscription.latest_invoice;
            const paymentIntent = latestInvoice.payment_intent;

            try {
                await handleStripeErrors(() => stripe.confirmPayment({
                    clientSecret: paymentIntent.client_secret,
                    confirmParams: {
                        return_url: window.location.href,
                        payment_method: paymentIntent.payment_method as string
                    },
                    redirect: "if_required"
                }));
                logger.metric(MetricName.PAYMENT_CONFIRMATION_ATTEMPT, {
                    success: true,
                    customerId: paymentIntent.customer,
                });
            } catch (err) {
                logger.metric(MetricName.PAYMENT_CONFIRMATION_ATTEMPT, {
                    success: false,
                    customerId: paymentIntent.customer,
                    errorMessage: err.message
                });
                throw err;
            }

            Analytics.Dunning.ConfirmSubscriptionChargeDialogPaymentConfirmed();
            onClose(true);
        } catch (err) {
            this.setState({ fetching: false });

            Analytics.Dunning.ConfirmSubscriptionChargeDialogPaymentConfirmationFailed();
            logger.error(err, "ConfirmSubscriptionChargeDialog stripe.handleConfirmPayment() failed");
            ShowErrorDialog({
                error: t("Could not confirm payment"),
                message: err.message
            });
        }
    }

    render() {
        const { fetching } = this.state;

        return (
            <MuiThemeProvider theme={dialogTheme}>
                <BeautifulDialog className="update_payment_form" preventClose>
                    <FetchingClickShield visible={fetching} backgroundColor="white" />
                    <DialogTitle>
                        <AlertContainer>
                            <Icon>error</Icon>
                            <span style={{ paddingLeft: 10 }}>{t("Please confirm your payment")}</span>
                        </AlertContainer>
                    </DialogTitle>
                    <PaddedDialogContent>
                        <DialogContentText>
                            {t("Your bank required additional verification to process your payment.")}
                        </DialogContentText>
                    </PaddedDialogContent>
                    <PaddedDialogActions>
                        <Button color="secondary" onClick={this.handleChangePaymentMethod}>
                            {t("Change payment method")}
                        </Button>
                        <Button
                            color="primary"
                            disabled={fetching}
                            onClick={this.handleConfirmPayment}>
                            {t("Confirm payment")}
                        </Button>
                    </PaddedDialogActions>
                </BeautifulDialog>
            </MuiThemeProvider >
        );
    }
}

export default withStripe(BillingController.withInitializedState(ConfirmSubscriptionChargeDialog));
